import type { SkyStarI } from "@/types/sky";

export const SKY_PREVIEW_SESSION_KEY = "fireflies-home-sky-preview:v1";
export const MAX_PREVIEW_STARS = 5;
export const SKY_PREVIEW_TTL_MS = 24 * 60 * 60 * 1000;

export type PreviewPlacement = {
  id: string;
  slot: number;
  dx: number;
  dy: number;
};
export type PreviewSession = {
  version: 1;
  revision: number;
  expiresAt: number;
  placements: PreviewPlacement[];
};
export type PreviewStar = PreviewPlacement & { star: SkyStarI };

// Only IDs and layout are kept for one day, never names/photos or private
// owner data. Memory fallback preserves navigation when storage is blocked.
let memorySession: PreviewSession | null = null;
let memoryOnly = false;

export function selectPreviewStars(stars: SkyStarI[]) {
  const pets = new Set<string>();
  const ids = new Set<string>();
  return stars.filter((star) => {
    if (
      star.public !== true ||
      !star.id ||
      ids.has(star.id) ||
      !star.pet_id ||
      !star.name?.trim() ||
      pets.has(star.pet_id)
    )
      return false;
    pets.add(star.pet_id);
    ids.add(star.id);
    return true;
  });
}

function validSession(value: unknown): value is PreviewSession {
  if (!value || typeof value !== "object") return false;
  const session = value as PreviewSession;
  if (
    session.version !== 1 ||
    !Number.isSafeInteger(session.revision) ||
    session.revision < 0 ||
    !Number.isFinite(session.expiresAt) ||
    session.expiresAt <= Date.now() ||
    !Array.isArray(session.placements) ||
    session.placements.length > MAX_PREVIEW_STARS
  )
    return false;
  const ids = new Set<string>(),
    slots = new Set<number>();
  return session.placements.every((item) => {
    if (
      !item ||
      typeof item.id !== "string" ||
      !item.id ||
      ids.has(item.id) ||
      !Number.isInteger(item.slot) ||
      item.slot < 0 ||
      item.slot >= MAX_PREVIEW_STARS ||
      slots.has(item.slot) ||
      !Number.isFinite(item.dx) ||
      Math.abs(item.dx) > 3 ||
      !Number.isFinite(item.dy) ||
      Math.abs(item.dy) > 3
    )
      return false;
    ids.add(item.id);
    slots.add(item.slot);
    return true;
  });
}

export function readPreviewSession(): PreviewSession | null {
  const fallback = () => (validSession(memorySession) ? memorySession : null);
  if (memoryOnly) return fallback();
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(SKY_PREVIEW_SESSION_KEY);
  } catch {
    return fallback();
  }
  if (!raw) return null;
  try {
    const session: unknown = JSON.parse(raw);
    return validSession(session) ? session : null;
  } catch {
    return null;
  }
}

export function writePreviewSession(session: PreviewSession | null) {
  memorySession = session;
  try {
    if (session)
      window.localStorage.setItem(
        SKY_PREVIEW_SESSION_KEY,
        JSON.stringify(session)
      );
    else window.localStorage.removeItem(SKY_PREVIEW_SESSION_KEY);
    memoryOnly = false;
  } catch {
    memoryOnly = true;
  }
}

function shuffled<T>(items: T[]): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index--) {
    const other = Math.floor(Math.random() * (index + 1));
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result;
}

export function createPreviewSession(
  stars: SkyStarI[],
  previous: PreviewSession | null = null
): PreviewSession {
  const publicStars = selectPreviewStars(stars);
  const previousIds = new Set(previous?.placements.map((item) => item.id));
  // Prefer new stories. A small real sky reuses real entries with a different
  // layout instead of fabricating extra stars or pet pages.
  const candidates = [
    ...shuffled(publicStars.filter((star) => !previousIds.has(star.id))),
    ...shuffled(publicStars.filter((star) => previousIds.has(star.id))),
  ].slice(0, MAX_PREVIEW_STARS);
  const revision = previous ? (previous.revision + 1) % 1000000 : 0;
  const direction = revision % 2 ? 1 : -1;
  return {
    version: 1,
    revision,
    expiresAt: Date.now() + SKY_PREVIEW_TTL_MS,
    placements: candidates.map((star, slot) => ({
      id: star.id,
      slot,
      dx: direction * (1 + Math.random() * 2),
      dy: direction * (1 + Math.random() * 2),
    })),
  };
}

export function resolvePreviewStars(
  session: PreviewSession,
  stars: SkyStarI[]
): PreviewStar[] {
  const available = new Map(
    selectPreviewStars(stars).map((star) => [star.id, star])
  );
  return session.placements.flatMap((placement) => {
    const star = available.get(placement.id);
    return star ? [{ ...placement, star }] : [];
  });
}
